import { Col, Form, Row, Tag, notification } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { FilterConfig, FilterValues } from 'types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'components/Auth/Auth';
import ConfirmModal from 'components/theme_components/ThirdParty/ConfirmModal';
import DataGrid from 'components/theme_components/DataGrid';
import ExportXLS from 'components/theme_components/ExportXLS';
import type { FormInstance } from 'antd/lib/form';
import HeroTitle from 'components/theme_components/HeroTitle';
import { findFinCTByAnyRefOrStatusOrDate } from 'services/fincontract';
import { findPropByAnyRefOrStatusOrDate } from 'services/proposal';
import { toCurrency } from 'utils/utils';
import { useQuery, useQueryClient } from 'react-query';

export type ReservationSubTabProps = {
  form: FormInstance;
  color?: string;
  tpauthid: number;
  tpid?: number;
  itemsCompanyHierarchyTree: any[];
  tptype?: string;
  isActive?: boolean; // New prop to track if tab is active
  onTabActivated?: () => void; // Callback when tab becomes active
};

const ReservationSubTab = (props: ReservationSubTabProps) => {
  const {
    form,
    color,
    tpauthid,
    tpid,
    itemsCompanyHierarchyTree,
    isActive,
    onTabActivated,
  } = props;
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [filterForm] = Form.useForm();
  const { isInspect } = useContext(AuthContext);
  const [modalVisibleConfirm, setmodalVisibleConfirmVisible] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const queryClient = useQueryClient();

  const useContracts = (
    searchParams: any,
    ctPhase: string,
    tpauthid: number,
    tpid?: number,
    isActive?: boolean
  ) => {
    const initSearchParams = tpid
      ? { ...searchParams, tpid: tpid }
      : searchParams;

    const proposalQuery = useQuery(
      [
        'findPropByAnyRefOrStatusOrDate',
        'accepted',
        searchParams,
        tpauthid,
        tpid,
      ],
      () =>
        findPropByAnyRefOrStatusOrDate({
          ...searchParams,
          tpid: tpid,
          // Don't filter by ctstatus for accepted reservations - show all
        })
          .then((res: { list: any[] }) => res.list)
          .catch((error) => {
            notification.error({
              description: error.message,
              message: 'Proposal Service is not available',
            });
            return [];
          }),
      {
        refetchOnMount: true,
        enabled: isActive !== false, // Enable query when tab is active
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        staleTime: 0,
      }
    );

    const fincontractQuery = useQuery(
      [
        'findFinCTByAnyRefOrStatusOrDate',
        'accepted',
        initSearchParams,
        tpauthid,
        tpid,
      ],
      () =>
        findFinCTByAnyRefOrStatusOrDate(initSearchParams).then(
          (res: { list: any[] }) => res.list
        ),
      {
        refetchOnMount: true,
        enabled: isActive !== false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        staleTime: 0,
      }
    );

    return ctPhase === 'PROPOSAL' ? proposalQuery : fincontractQuery;
  };

  const {
    data: proposalData,
    isFetching,
    refetch,
  } = useContracts(searchParams, 'PROPOSAL', tpauthid, tpid, isActive);

  // Effect to handle tab activation
  useEffect(() => {
    if (isActive) {
      // Refetch data when tab becomes active
      refetch();
      onTabActivated?.();
    }
  }, [isActive, refetch, onTabActivated]);

  // Effect to invalidate queries when tpauthid or tpid changes
  useEffect(() => {
    queryClient.invalidateQueries([
      'findPropByAnyRefOrStatusOrDate',
      'accepted',
    ]);
    queryClient.invalidateQueries([
      'findFinCTByAnyRefOrStatusOrDate',
      'accepted',
    ]);
  }, [tpauthid, tpid, queryClient]);

  // Reset state when third party changes
  useEffect(() => {
    setSearchParams({});
    filterForm.resetFields();
    setSelectedRows([]);
  }, [tpauthid, tpid, filterForm]);
  const filterConfig: FilterConfig[] = [
    { key: 'propreference', label: 'N° cars', type: 'text' },
    { key: 'vin', label: 'VIN1/VIN2', type: 'text' },
    {
      key: 'PRCODE',
      label: 'Famille produit',
      type: 'select',
    },
    { key: 'REFREASON', type: 'select', label: 'ctstatus' },

    { key: 'ctstatusdate', label: 'Date de décision', type: 'date' },
    {
      key: 'minremaining',
      label: 'Montant mini (€)',
      type: 'number',
    },
    {
      key: 'maxremaining',
      label: 'Montant maxi (€)',
      type: 'number',
    },
  ];
  const filterConfigGlobal: FilterConfig[] = [
    { key: 'tpreference', label: 'RRDI', type: 'text' },
    { key: 'clientname', label: 'Concession', type: 'text' },

    {
      key: 'PRCODE',
      label: 'Famille produit',
      type: 'select',
    },
    { key: 'propreference', label: 'N° cars', type: 'text' },
    { key: 'vin', label: 'VIN1/VIN2', type: 'text' },
    { key: 'ctstatus', label: 'ctstatus', type: 'text' },

    { key: 'ctstatusdate', label: 'Date de décision', type: 'date' },
    {
      key: 'minremaining',
      label: 'Montant mini (€)',
      type: 'number',
    },
    {
      key: 'maxremaining',
      label: 'Montant maxi (€)',
      type: 'number',
    },
  ];
  const handleFilterSearch = useCallback((values: FilterValues) => {
    const formattedValues = {
      ...values,
      ctstatus: values.ctstatus,
      ctstatusdate: values.ctstatusdate
        ? values.ctstatusdate.format('YYYY-MM-DD')
        : '',
    };
    setSearchParams(formattedValues);
  }, []);

  const handleFilterReset = useCallback(() => {
    filterForm.resetFields();
    setSearchParams({});
  }, [filterForm]);

  const columns = [
    ...(!props.tptype
      ? [
          { dataIndex: 'tpreference', key: 'tpreference' },
          { dataIndex: 'clientname', key: 'clientname' },
        ]
      : []),
    {
      dataIndex: 'propreference',
      key: 'cars',
    },
    {
      dataIndex: 'vin1',
      key: 'vin1',
    },
    {
      dataIndex: 'vin2',
      key: 'vin2',
    },

    {
      dataIndex: 'prcodelabel',
      key: 'familleProduit',
    },
    {
      dataIndex: 'ctstatus',
      key: 'ctstatus',
      render: (ctstatus: string, record: any) => (
        <Tag
          color={ctstatus === 'ACCEPT' ? '#D6E9E6' : 'orange'}
          className="tag-status tag-status-default ml-2 p-2"
          bordered={false}
          icon={
            ctstatus === 'ACCEPT' ? (
              <CheckOutlined color="#0A7C53" />
            ) : (
              <CloseOutlined color="#DD7248" />
            )
          }
        >
          {record.statuslabel}
        </Tag>
      ),
    },
    {
      dataIndex: 'ctstatusdate',
      key: 'ctstatusdate',
      sorter: true,
    },
    // {
    //   dataIndex: "ctepartdays",
    //   key: "ctepartdays",
    // },

    ...(props.tptype
      ? [
          {
            dataIndex: 'ctfinancedamount',
            key: 'ctfinancedamount',

            render: (value: number, record: any) => toCurrency(value),
          },
        ]
      : [
          {
            dataIndex: 'ctfinancedamount',
            key: 'ctfinancedamount',
          },
        ]),
  ];

  const colsXLS = [
    'propreference',
    'vin1',
    'vin2',
    'prcodelabel',
    'ctstatus',
    'refreason',
    'ctstatusdate',
    'ctepartdays',
    'ctfinancedamount',
  ];

  return (
    <Form form={form} className="bg-color-gray">
      <div className="bg-color-gray">
        <HeroTitle
          form={filterForm}
          color={color || 'purple'}
          title="Liste des CARs acceptées"
          display
          showFilter
          filterConfig={!props.tptype ? filterConfigGlobal : filterConfig}
          onFilterSearch={handleFilterSearch}
          onFilterReset={handleFilterReset}
          className="productlist"
          noTitleOverlay
          itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
        />
      </div>
      <div className="ut-title-page no-after">
        <Row justify="space-between" align="middle" className="mb-4 mt-6">
          <Col></Col>
          <Col span={5} className="d-flex justify-end align-center">
            <ExportXLS
              data={proposalData || []}
              filename="reservations"
              columns={colsXLS}
            />
          </Col>
        </Row>
        <Row className="w-100 mb-1 d-flex align-center">
          <Col span={24}>
            <Form.Item name="filteredData" initialValue={proposalData}>
              <DataGrid
                columns={columns}
                dataSource={proposalData || []}
                size="large"
                className="custom-data-grid"
                rowKey="ctrid"
                isLoading={isFetching}
                // rowSelection={{
                //   type: "checkbox",
                //   onChange: (selectedRowKeys, selectedRows) => {
                //     setSelectedRows(selectedRows)
                //   },
                //   hideSelectAll: false,
                //   columnWidth: 80,
                // }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} sur ${total}`,
                }}
                scrollInput={{ x: 1000, y: 550 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <ConfirmModal
          className="modalConfirmOrCancel"
          visible={modalVisibleConfirm}
          status="success"
          message="Demande d'acceptation."
          description="Votre demande a été bien prise en charge."
          onConfirm={() => {
            !isInspect && setmodalVisibleConfirmVisible(false);
          }}
          Confirmcomcode="close"
          onlyConfirm
        />
      </div>
    </Form>
  );
};

export default ReservationSubTab;
